(() => {
  const fallbackMessages = {
    bounds: 'Choose a dimension between __min__ and __max__ cm for this configuration.',
    documents: '__count__ of __total__ documents',
    compare_selected: 'Compare selected (__count__/5)',
    results: '__count__ of __total__ products',
    product: 'Product', best: 'Best fit', difference: 'Difference',
    see_specs: 'See model specifications', confirm: 'Confirm for your project',
    listed_formats: 'Listed formats', weight: 'Weight', frame_material: 'Frame / profile material',
    print_material: 'Print material', depth: 'Profile depth', minimum: 'Minimum size',
    maximum: 'Maximum size', unspecified: 'Not specified', configuration: 'Selected configuration',
    choose_model: 'Choose on the model page', details: 'Details',
    view_specs: 'View product and specifications', location: 'Location',
    template_context: 'help with print files / template'
  };
  function messagesFor(element) {
    try {
      return { ...fallbackMessages, ...JSON.parse(element.closest('[data-messages]')?.dataset.messages || '{}') };
    } catch {
      return fallbackMessages;
    }
  }
  function message(messages, key, values = {}) {
    return messages[key].replace(/__(\w+)__/g, (token, name) => values[name] ?? token);
  }
  function contactLink(form, product) {
    const url = new URL(form.action || '/pages/contact', window.location.origin);
    url.searchParams.delete('product');
    url.hash = new URLSearchParams({ product: product.trim() }).toString();
    return url.href;
  }
  function displayConfiguration(card, fallback) {
    const form = card.querySelector('form');
    if (!form) return fallback;
    const width = form.querySelector('[data-width]'), height = form.querySelector('[data-height]');
    const choices = Array.from(form.querySelectorAll('[data-choice]')).filter(input => input.value).map(input => {
      const label = input.selectedOptions?.[0]?.textContent.trim() || input.value.trim();
      const prefix = input === width ? 'W ' : input === height ? 'H ' : '';
      const unit = input.dataset.unitLabel || (prefix ? 'cm' : input.dataset.unit || '');
      return prefix + label + (unit ? ` ${unit}` : '');
    });
    return [card.dataset.title, ...choices].filter(Boolean).join(' - ');
  }
  function updateOptions(form) {
    const messages = messagesFor(form);
    const size = form.querySelector('[data-size]');
    const pack = form.querySelector('[data-package]');
    if (form.dataset.handle === 'straight-zipper-wall' && pack) {
      const coverOnly = /^(?:W )?500 x/.test(size?.value || '');
      Array.from(pack.options).forEach(option => { option.disabled = coverOnly && option.value === 'Frame and printed cover'; });
      if (coverOnly) pack.value = 'Replacement textile cover';
    }
    const width = form.querySelector('[data-width]');
    const height = form.querySelector('[data-height]');
    const errors = [];
    if (width && height) {
      const minShort = Number(form.dataset.minShort), minLong = Number(form.dataset.minLong);
      const maxShort = Number(form.dataset.maxShort), maxLong = Number(form.dataset.maxLong);
      [[width,height],[height,width]].forEach(([input,other])=>{
        const peer=Number(other.value), value=Number(input.value);
        input.min=String(peer>0&&peer<minLong?minLong:minShort);
        input.max=String(peer>maxShort?maxShort:maxLong);
        const invalid=input.value!==''&&(value<Number(input.min)||value>Number(input.max));
        const errorMessage=invalid?message(messages,'bounds',{min:input.min,max:input.max}):'';
        input.setCustomValidity(errorMessage);
        if(errorMessage)errors.push(errorMessage);
      });
    }
    const choices=Array.from(form.querySelectorAll('[data-choice]')).filter(input=>input.value).map(input=>{
      const label=input.selectedOptions?.[0]?.textContent.trim() || input.value.trim();
      const unit=input.dataset.unitLabel || input.dataset.unit;
      return (input===width?'W ':input===height?'H ':'')+label+(unit?` ${unit}`:'');
    });
    const context=[form.dataset.title,...choices].join(' - ');
    form.querySelector('[name="product"]').value=context;
    const template=form.querySelector('[data-template]');
    if(template)template.href=contactLink(form,context+' - '+messages.template_context);
    const error=form.querySelector('[data-option-error]');
    if(error){error.textContent=[...new Set(errors)].join(' ');error.hidden=!errors.length;}
    const owner=form.closest('[data-range-card]')||form.closest('[data-range-page]');
    owner?.querySelectorAll('[data-card-quote],[data-range-page-quote]').forEach(link=>{link.href=contactLink(form,context);});
    return context;
  }
  let downloadListId = 0;
  function initDownloads(section) {
    if (section.dataset.downloadReady) return;
    const links = Array.from(section.querySelectorAll('[data-download]'));
    const list = section.querySelector('ul');
    const button = section.querySelector('[data-download-toggle]');
    const status = section.querySelector('[data-download-status]');
    if (!links.length || !list || !button || !status) return;
    section.dataset.downloadReady = 'true';
    const search = section.querySelector('[data-download-search]');
    const label = button.querySelector('[data-download-toggle-label]');
    list.id = list.id || 'ns-download-list-' + (++downloadListId);
    button.setAttribute('aria-controls', list.id);
    let expanded = false;
    const render = () => {
      const term = search?.value.trim().toLowerCase() || '';
      const matching = links.filter(link => (link.dataset.search || link.textContent).toLowerCase().includes(term));
      const shown = term ? matching : expanded ? links : links.slice(0, 5);
      const visible = new Set(shown);
      links.forEach((link, index) => {
        link.hidden = !visible.has(link);
        link.toggleAttribute('data-download-continuation', !term && !expanded && links.length > 5 && index === 4);
      });
      button.hidden = links.length <= 5 || !!term;
      button.setAttribute('aria-expanded', String(expanded));
      label.textContent = expanded ? label.dataset.showFewer : label.dataset.showAll;
      const format = term ? section.dataset.downloadMatches : section.dataset.downloadCount;
      status.textContent = format.replace(/__(\w+)__/g, (token, name) => ({count: shown.length, total: links.length})[name] ?? token);
      status.hidden = !term && links.length <= 5 && !search;
    };
    button.addEventListener('click', () => {
      const collapsing = expanded;
      expanded = !expanded;
      render();
      if (collapsing) window.nordsignKeepSectionInView?.(section);
    });
    search?.addEventListener('input', render);
    render();
  }
  class RangeOptions extends HTMLElement {
    connectedCallback(){
      if(this.initialized)return;this.initialized=true;
      this.querySelectorAll('.ns-range-documents').forEach(initDownloads);
      const form=this.querySelector('form');if(!form)return;
      const update=()=>updateOptions(form);
      form.addEventListener('input',update);form.addEventListener('change',update);
      form.addEventListener('submit',event=>{update();if(!form.reportValidity())event.preventDefault();});
      const owner=form.closest('[data-range-card]')||form.closest('[data-range-page]')||this;
      owner.addEventListener('click',event=>{if(event.target.closest('[data-template],[data-card-quote],[data-range-page-quote]')){update();if(!form.reportValidity())event.preventDefault();}});
      update();

    }
  }
  class Range extends HTMLElement {
    connectedCallback(){
      if(this.initialized)return;this.initialized=true;
      this.messages=messagesFor(this);
      this.cards=Array.from(this.querySelectorAll('[data-range-card]'));
      this.search=this.querySelector('[data-range-search]');
      this.type=this.querySelector('[data-range-type]');this.use=this.querySelector('[data-range-use]');
      this.compare=this.querySelector('[data-range-compare]');this.dialog=this.querySelector('dialog');
      if(!this.search){this.querySelectorAll('.ns-range__compare').forEach(x=>x.hidden=true);return;}
      this.querySelectorAll('[data-range-tools]').forEach(x=>x.hidden=false);
      [['type',this.type],['use',this.use]].forEach(([key,select])=>{
        const values=[...new Set(this.cards.map(c=>c.dataset[key]).filter(Boolean))].sort();
        values.forEach(value=>{const card=this.cards.find(c=>c.dataset[key]===value);select.add(new Option(card.dataset[`${key}Label`]||value,value));});
        this.querySelector(`[data-${key}-label]`).hidden=values.length<2;
        select.addEventListener('change',()=>this.filter());
      });
      this.search.addEventListener('input',()=>this.filter());
      this.querySelector('[data-range-reset]').addEventListener('click',()=>{this.search.value='';this.type.value='';this.use.value='';this.filter();});
      this.querySelectorAll('[data-compare]').forEach(box=>box.addEventListener('change',()=>this.updateComparison()));
      this.compare.addEventListener('click',()=>this.showComparison());
      this.querySelector('[data-range-close]').addEventListener('click',()=>this.dialog.close());
      this.dialog.addEventListener('click',event=>{const r=this.dialog.getBoundingClientRect();if(event.target===this.dialog&&(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom))this.dialog.close();});
      this.dialog.addEventListener('close',()=>this.compare.focus());
      this.filter();
    }
    selected(){return this.cards.filter(c=>c.querySelector('[data-compare]').checked);}
    filter(){
      const term=this.search.value.trim().toLowerCase();
      this.cards.forEach(card=>{
        card.hidden=!card.dataset.search.includes(term)||(this.type.value&&card.dataset.type!==this.type.value)||(this.use.value&&card.dataset.use!==this.use.value);
        if(card.hidden)card.querySelector('[data-compare]').checked=false;
      });
      const count=this.cards.filter(c=>!c.hidden).length;
      this.querySelector('[data-range-result]').textContent=message(this.messages,'results',{count,total:this.cards.length});
      this.querySelector('[data-range-empty]').hidden=count!==0;this.updateComparison();
    }
    updateComparison(){const count=this.selected().length;this.compare.textContent=message(this.messages,'compare_selected',{count});this.compare.disabled=count<2;this.querySelectorAll('[data-compare]').forEach(box=>box.disabled=!box.checked&&count>=5);}
    showComparison(){
      const cards=this.selected();if(cards.length<2||cards.length>5)return;
      const text=key=>message(this.messages,key);
      const table=document.createElement('table');
      const row=(label,values,header=false)=>{const tr=document.createElement('tr');const th=document.createElement('th');th.scope='row';th.textContent=label;tr.append(th);values.forEach(value=>{const cell=document.createElement(header?'th':'td');if(header)cell.scope='col';if(typeof value==='string')cell.textContent=value;else cell.append(value);tr.append(cell);});table.append(tr);};
      row(text('product'),cards.map(c=>c.dataset.title),true);
      row(text('best'),cards.map(c=>c.querySelector('[data-summary]').textContent));
      row(text('difference'),cards.map(c=>c.querySelector('.ns-range__difference')?.textContent||text('see_specs')));
      row(text('location'),cards.map(c=>c.dataset.useLabel||c.dataset.use||text('confirm')));
      row(text('listed_formats'),cards.map(c=>Array.from(c.querySelector('[data-size]')?.options||[]).filter(o=>o.value).map(o=>o.textContent.trim()).join('; ')||text('see_specs')));
      const technicalRows=[['weight',/^(roll up weight|weight)$/i],['frame_material',/^(frame material|frame and feet materials|material)$/i],['print_material',/^(textile material|banner material|print and material)$/i],['depth',/^(thickness|system thickness)$/i],['minimum',/^minimum size$/i],['maximum',/^maximum size$/i]];
      technicalRows.forEach(([key,pattern])=>{const values=cards.map(card=>{const term=Array.from(card.querySelectorAll('.ns-range-options__specs dt')).find(dt=>pattern.test((dt.dataset.specKey||'').trim()));return term?.nextElementSibling?.textContent.trim()||'';});if(values.some(Boolean))row(text(key),values.map(value=>value||text('unspecified')));});
      row(text('configuration'),cards.map(c=>displayConfiguration(c,text('choose_model'))));
      row(text('details'),cards.map(c=>{const link=c.querySelector('[data-product-link]').cloneNode(true);link.textContent=text('view_specs');return link;}));
      this.querySelector('[data-range-table]').replaceChildren(table);this.dialog.showModal();
    }
  }
  if(!customElements.get('nordsign-range-options'))customElements.define('nordsign-range-options',RangeOptions);
  if(!customElements.get('nordsign-range'))customElements.define('nordsign-range',Range);
})();
